import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Analytics } from '../../analytics';
import type { MessageSourceType } from '../../analytics/types';
import { MAX_USER_MESSAGE_CHARS } from '../../config/constants';
import { useMobileKeyboardAdjustment } from '../../hooks/ui/useMobileKeyboardAdjustment';
import { isMobileDevice } from '../../utils/device/mobileDetection';
import ArrowUpIcon from '../ArrowUpIcon';
import Shimmer from '../Shimmer';
import SparkleIcon from '../SparkleIcon';
import SearchBarPlaceholder from './SearchBarPlaceholder';
import SendButtonLoadingDots from './SendButtonLoadingDots';

interface SearchBarInputFieldProps {
    message: string;
    onMessageChange: (message: string) => void;
    onKeyPress: (e: React.KeyboardEvent) => void;
    onSendMessage: (messageSource?: MessageSourceType) => void;
    isLoading: boolean;
    primaryColor: string;
    showPlaceholder: boolean;
    placeholder: string;
    showShimmer: boolean;
    autoFocus?: boolean; // Auto-focus input on mount (e.g., when expanding from collapsed view)
    onBlurWithoutText?: () => void; // Called when input blurs AND no text was entered (for collapse-to-minimize)
    onEngage?: () => void;
}

const SearchBarInputField: React.FC<SearchBarInputFieldProps> = ({
    message,
    onMessageChange,
    onKeyPress,
    onSendMessage,
    isLoading,
    primaryColor,
    showPlaceholder,
    placeholder,
    showShimmer,
    autoFocus = false,
    onBlurWithoutText,
    onEngage,
}) => {
    // Track if input is focused
    const [isInputFocused, setIsInputFocused] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);
    const autoFocusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Auto-focus input on mount when autoFocus is true (e.g., expanding from collapsed view)
    useEffect(() => {
        if (!autoFocus) return;

        // Clear any pending focus timer before scheduling a new one.
        if (autoFocusTimerRef.current !== null) {
            clearTimeout(autoFocusTimerRef.current);
        }

        // Small delay to ensure the component is fully rendered and animations complete
        autoFocusTimerRef.current = setTimeout(() => {
            inputRef.current?.focus({ preventScroll: true });
            autoFocusTimerRef.current = null;
        }, 100);
    }, [autoFocus]);

    // Cleanup on unmount to avoid focusing after the input is gone.
    useEffect(() => {
        return () => {
            if (autoFocusTimerRef.current !== null) {
                clearTimeout(autoFocusTimerRef.current);
            }
        };
    }, []);

    // Track if user has typed anything in this session
    const hasTypedRef = useRef(false);

    const { onFocus: handleMobileFocus, onBlur: handleMobileBlur } =
        useMobileKeyboardAdjustment(inputRef);

    const handleInputFocus = () => {
        onEngage?.();
        setIsInputFocused(true);
        handleMobileFocus();
    };

    const handleInputBlur = () => {
        setIsInputFocused(false);
        handleMobileBlur();

        // Notify parent if blurring with no text (for collapse-to-minimize behavior in copilot mode)
        if (message.trim().length === 0 && onBlurWithoutText) {
            onBlurWithoutText();
        }
    };

    const handleInputChange = (value: string) => {
        // Guard against oversized pastes: most browsers honor the <input maxLength>
        // attribute on keypress, but a programmatic setter or a stale state write
        // can still push past it. Truncate defensively.
        const clamped =
            value.length > MAX_USER_MESSAGE_CHARS ? value.slice(0, MAX_USER_MESSAGE_CHARS) : value;
        if (clamped.length > 0) {
            onEngage?.();
        }
        onMessageChange(clamped);

        // Track when user first starts typing (but only once per session)
        // Uses analytics context to get domain/sessionId automatically
        if (!hasTypedRef.current && clamped.length > 0) {
            hasTypedRef.current = true;
            // Fire and forget - don't wait for tracking to complete
            Analytics.trackTypedSomethingIntoInput({
                inputLength: clamped.length,
                isFirstInput: true,
            });
        }
    };

    const handleSendClick = () => {
        // Blur input on mobile to close virtual keyboard on send.
        if (isMobileDevice()) {
            if (inputRef.current) {
                inputRef.current.blur();
            } else {
                const active = document.activeElement as HTMLElement | null;
                if (active && typeof active.blur === 'function') active.blur();
            }
        }
        onSendMessage();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Call parent handler for Enter handling and analytics
        onKeyPress(e);
        // On Enter, blur on mobile to close virtual keyboard (allow parent to process first)
        if (e.key === 'Enter' && !e.shiftKey && isMobileDevice()) {
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.blur();
                } else {
                    const active = document.activeElement as HTMLElement | null;
                    if (active && typeof active.blur === 'function') active.blur();
                }
            }, 0);
        }
    };

    const hasSendableMessage = message.trim().length > 0;
    const canSend = hasSendableMessage && !isLoading;

    return (
        <div
            className={`rose-search-bar-input-field flex items-center flex-1 min-w-0 gap-2 px-2 py-1 h-10 relative ${
                showShimmer && !isInputFocused ? 'overflow-hidden' : ''
            }`}
            style={{
                backgroundColor: 'var(--rw-surface-input)',
                border: '1px solid var(--rw-border-subtle)',
                borderRadius: 'var(--rw-radius-control-compact)',
            }}
        >
            <style>{`
                .rose-message-input::placeholder {
                    color: transparent;
                    font-weight: var(--rw-font-weight-placeholder);
                    letter-spacing: normal;
                }

                @media (min-width: 768px) {
                    .rose-message-input::placeholder {
                        color: var(--rw-text-placeholder);
                    }
                }

                .rose-message-send-button[data-ph-capture-attribute-enabled='true']:hover {
                    background-color: var(--rw-surface-interactive-hover);
                }

                .rose-message-send-button[data-ph-capture-attribute-enabled='true']:active {
                    background-color: var(--rw-surface-interactive-active);
                }
            `}</style>
            {/* Shimmer effect - only show when showShimmer is true AND input is not focused */}
            {showShimmer && !isInputFocused && (
                <Shimmer
                    primaryColor={primaryColor}
                    animationSpeed={2} // Animation takes 2 seconds
                    animationPause={8} // Pause for 8 seconds between animations
                />
            )}

            {/* Sparkle Icon */}
            <SparkleIcon color={primaryColor} size={20} />

            {/* Input Field with Custom Scrolling Placeholder */}
            <div className="flex-1 min-w-0 relative">
                <input
                    className="rose-message-input w-full border-none outline-none bg-transparent p-0 placeholder-transparent"
                    style={{
                        color: 'var(--rw-text-primary)',
                        fontSize: 'var(--rw-font-size-body)',
                        fontWeight: 'var(--rw-font-weight-input)',
                        letterSpacing: 'normal',
                        lineHeight: 'var(--rw-line-height-input)',
                    }}
                    type="text"
                    ref={inputRef}
                    value={message}
                    onChange={(e) => handleInputChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    placeholder={showPlaceholder ? placeholder : ''}
                    maxLength={MAX_USER_MESSAGE_CHARS}
                    enterKeyHint="send"
                    autoComplete="off"
                    autoCapitalize="sentences"
                />

                {/* Custom Scrolling Placeholder */}
                {!isInputFocused && message.length === 0 && showPlaceholder && (
                    <SearchBarPlaceholder placeholder={placeholder} />
                )}
            </div>

            {/* Send Button */}
            <button
                type="button"
                className={`rose-message-send-button border-none bg-none p-1 flex items-center justify-center transition-all duration-150 group ${
                    canSend ? 'cursor-pointer opacity-100 active:scale-95' : 'cursor-not-allowed'
                }`}
                data-ph-capture-attribute-button-type="send-message"
                data-ph-capture-attribute-enabled={canSend}
                onClick={handleSendClick}
                disabled={!canSend}
                aria-label={isLoading ? 'Sending...' : 'Send message'}
            >
                {isLoading ? (
                    <SendButtonLoadingDots color="var(--rw-accent)" />
                ) : (
                    <ArrowUpIcon
                        width={24}
                        height={24}
                        backgroundColor={
                            hasSendableMessage
                                ? 'var(--rw-bg-action-primary)'
                                : 'var(--rw-surface-input)'
                        }
                        color={
                            hasSendableMessage
                                ? 'var(--rw-text-on-action-primary)'
                                : 'var(--rw-text-placeholder)'
                        }
                        className="transition-all duration-150 group-active:scale-90"
                    />
                )}
            </button>
        </div>
    );
};

export default SearchBarInputField;
